import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { toast } from 'sonner';
import {
  Webhook, Key, RefreshCw, Copy, Eye, EyeOff, Check, 
  AlertTriangle, Clock, FileText, ExternalLink, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminWebhooks() {
  const [loading, setLoading] = useState(true);
  const [secretInfo, setSecretInfo] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  
  // Generate secret dialog
  const [generateDialog, setGenerateDialog] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [newSecret, setNewSecret] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Rate Limits
  const [rateLimits, setRateLimits] = useState(null);

  useEffect(() => {
    fetchSecretInfo();
    fetchLogs();
    fetchRateLimits();
  }, []);

  const fetchSecretInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/webhook/orders/secret`);
      if (response.ok) {
        const data = await response.json();
        setSecretInfo(data);
      }
    } catch (error) {
      console.error('Error fetching secret info:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/webhook/orders/logs?limit=20`);
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchRateLimits = async () => {
    try {
      const response = await fetch(`${API_URL}/api/webhook/orders/rate-limits`);
      if (response.ok) {
        const data = await response.json();
        setRateLimits(data);
      }
    } catch (error) {
      console.error('Error fetching rate limits:', error);
    }
  };

  const handleGenerateSecret = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/webhook/orders/generate-secret`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('Failed to generate');
      
      const data = await response.json();
      setNewSecret(data.secret);
      setShowSecret(true);
      toast.success('Neuer Secret generiert');
      
      // Refresh info
      fetchSecretInfo();
    } catch (error) {
      toast.error('Fehler beim Generieren');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopySecret = () => {
    if (newSecret) {
      navigator.clipboard.writeText(newSecret);
      setCopied(true);
      toast.success('Secret kopiert!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    if (status === 'success') {
      return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Erfolg</Badge>;
    } else if (status === 'duplicate') {
      return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Duplikat</Badge>;
    } else {
      return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Fehler</Badge>;
    }
  };

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 
          className="text-2xl md:text-3xl font-bold flex items-center gap-3"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          data-testid="admin-webhooks-title"
        >
          <Webhook className="h-8 w-8 text-[#2F80FF]" />
          Webhook-Einstellungen
        </h1>
        <p className="text-slate-400 mt-1">Verwalten Sie externe Integrationen</p>
      </div>

      {/* Secret Management */}
      <Card 
        className="p-6 mb-6" 
        style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Key className="h-5 w-5 text-amber-400" />
              Order Webhook Secret
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              HMAC-SHA256 Secret für die Signatur-Verifizierung
            </p>
          </div>
          <Button
            onClick={() => setGenerateDialog(true)}
            data-testid="generate-secret-btn"
            className="bg-[#2F80FF] hover:bg-[#4A92FF] text-white"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Neuen Secret generieren
          </Button>
        </div>

        {loading ? (
          <Skeleton className="h-24 w-full bg-slate-700 rounded-lg" />
        ) : secretInfo?.status === 'configured' ? (
          <div className="p-4 rounded-lg" style={{ backgroundColor: '#0E1628' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Aktueller Secret (Preview)</p>
                <p className="font-mono text-lg mt-1">{secretInfo.secret_preview}</p>
              </div>
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                <Check className="h-3 w-3 mr-1" />
                Konfiguriert
              </Badge>
            </div>
            {secretInfo.updated_at && (
              <p className="text-xs text-slate-500 mt-2">
                Zuletzt aktualisiert: {formatDate(secretInfo.updated_at)}
              </p>
            )}
          </div>
        ) : (
          <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <div className="flex items-center gap-2 text-amber-400">
              <AlertTriangle className="h-5 w-5" />
              <span>Kein Webhook-Secret konfiguriert</span>
            </div>
            <p className="text-sm text-slate-400 mt-2">
              Ohne Secret werden Webhooks ohne Authentifizierung akzeptiert.
              Generieren Sie einen Secret für sichere Kommunikation.
            </p>
          </div>
        )}

        {/* Usage Info */}
        <div className="mt-4 p-4 rounded-lg border border-slate-700">
          <h3 className="text-sm font-medium mb-2">Verwendung</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-slate-400">Endpoint:</span>
              <code className="bg-slate-800 px-2 py-0.5 rounded text-[#2F80FF]">
                POST /api/webhook/orders
              </code>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-400">Header:</span>
              <code className="bg-slate-800 px-2 py-0.5 rounded">
                X-Webhook-Signature: sha256=&lt;hmac_hex&gt;
              </code>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-400">Algorithmus:</span>
              <span>HMAC-SHA256</span>
            </div>
          </div>
          <a 
            href="/api/webhook/orders/format-info" 
            target="_blank"
            className="inline-flex items-center gap-1 text-sm text-[#2F80FF] hover:underline mt-3"
          >
            <FileText className="h-4 w-4" />
            API-Dokumentation anzeigen
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </Card>

      {/* Rate Limits */}
      {rateLimits && rateLimits.sources && rateLimits.sources.length > 0 && (
        <Card 
          className="p-6 mb-6" 
          style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              Rate-Limits
            </h2>
            <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
              {rateLimits.limits.per_minute}/min, {rateLimits.limits.per_hour}/h
            </Badge>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rateLimits.sources.map((source) => (
              <div 
                key={source.source}
                className="p-4 rounded-lg"
                style={{ backgroundColor: '#0E1628' }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{source.source}</span>
                  {source.minute.remaining < 10 && (
                    <Badge className="bg-red-500/20 text-red-400 text-xs">
                      Fast am Limit
                    </Badge>
                  )}
                </div>
                <div className="space-y-2">
                  <div>
                    <div className="flex justify-between text-xs text-slate-400 mb-1">
                      <span>Minute</span>
                      <span>{source.minute.used}/{source.minute.limit}</span>
                    </div>
                    <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${
                          source.minute.used / source.minute.limit > 0.8 
                            ? 'bg-red-500' 
                            : 'bg-[#2F80FF]'
                        }`}
                        style={{ width: `${(source.minute.used / source.minute.limit) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-slate-400 mb-1">
                      <span>Stunde</span>
                      <span>{source.hour.used}/{source.hour.limit}</span>
                    </div>
                    <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${
                          source.hour.used / source.hour.limit > 0.8 
                            ? 'bg-red-500' 
                            : 'bg-emerald-500'
                        }`}
                        style={{ width: `${(source.hour.used / source.hour.limit) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Recent Webhook Logs */}
      <Card 
        className="p-6" 
        style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Clock className="h-5 w-5 text-slate-400" />
            Letzte Webhooks
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchLogs}
            disabled={logsLoading}
            className="text-slate-400 hover:text-white"
          >
            {logsLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>

        {logsLoading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full bg-slate-700 rounded" />
            ))}
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <Webhook className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>Noch keine Webhooks empfangen</p>
            <p className="text-sm mt-1">
              Nutzen Sie den Test-Endpoint zum Ausprobieren
            </p>
          </div>
        ) : (
          <Accordion type="single" collapsible className="space-y-2">
            {logs.map((log, index) => (
              <AccordionItem 
                key={log.webhook_id || index} 
                value={log.webhook_id || `log-${index}`}
                className="border border-slate-700 rounded-lg px-4"
                style={{ backgroundColor: '#0E1628' }}
              >
                <AccordionTrigger className="hover:no-underline py-3">
                  <div className="flex items-center gap-4 w-full">
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{log.external_order_id || 'N/A'}</span>
                        <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                          {log.external_source || 'unknown'}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {formatDate(log.received_at)}
                      </p>
                    </div>
                    {getStatusBadge(log.status)}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-3">
                  <div className="text-sm space-y-2">
                    {log.order_id && (
                      <div className="flex gap-2">
                        <span className="text-slate-400">Order ID:</span>
                        <code className="text-slate-300">{log.order_id}</code>
                      </div>
                    )}
                    {log.error && (
                      <div className="p-2 rounded bg-red-500/10 border border-red-500/30 text-red-400">
                        {log.error}
                      </div>
                    )}
                    {log.payload && (
                      <details className="cursor-pointer">
                        <summary className="text-slate-400 hover:text-white">
                          Payload anzeigen
                        </summary>
                        <pre className="mt-2 p-2 rounded bg-slate-800 text-xs overflow-auto max-h-48">
                          {JSON.stringify(log.payload, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </Card>

      {/* Generate Secret Dialog */}
      <Dialog open={generateDialog} onOpenChange={setGenerateDialog}>
        <DialogContent className="bg-[#111C32] border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-amber-400" />
              {newSecret ? 'Neuer Secret generiert' : 'Neuen Secret generieren'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {newSecret 
                ? 'Speichern Sie den Secret sicher. Er wird nur einmal angezeigt!'
                : 'Der aktuelle Secret wird ungültig und durch einen neuen ersetzt.'
              }
            </DialogDescription>
          </DialogHeader>

          {newSecret ? (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-emerald-400">Ihr neuer Secret:</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowSecret(!showSecret)}
                    className="text-slate-400 hover:text-white"
                  >
                    {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono text-sm bg-slate-800 p-2 rounded overflow-x-auto">
                    {showSecret ? newSecret : '••••••••••••••••••••••••••••••••'}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopySecret}
                    className="border-slate-600 text-slate-300 hover:bg-slate-800"
                  >
                    {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="p-3 rounded bg-amber-500/10 border border-amber-500/30">
                <p className="text-sm text-amber-400">
                  ⚠️ Dieser Secret wird nicht erneut angezeigt. Speichern Sie ihn jetzt!
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <p className="text-sm text-amber-400">
                ⚠️ <strong>Achtung:</strong> Der aktuelle Secret wird ungültig.
                Alle externen Systeme müssen den neuen Secret verwenden.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setGenerateDialog(false);
                setNewSecret(null);
                setShowSecret(false);
              }}
            >
              {newSecret ? 'Schließen' : 'Abbrechen'}
            </Button>
            {!newSecret && (
              <Button
                onClick={handleGenerateSecret}
                disabled={generating}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                {generating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Secret generieren
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
